<template class="flex flex-col h-[36rem] w-full">
    <div class="flex justify-between items-center mb-4 gap-6">
        <button
            @click="addImgModalOpened = !addImgModalOpened"
            class="flex items-center gap-2 w-1/2 border border-gray-400 hover:bg-gray-300 text-gray-700 px-4 py-3 rounded"
        >
            <IconAddFile class="ml-3"/>
            <span class="font-medium">{{ addImageText }}</span>
        </button>
        <template v-if="addTextLoading">
            <div
                class="flex items-center justify-center gap-2 w-1/2 border border-gray-400 rounded px-4 py-3 bg-gray-50"
            >
                <svg
                    class="h-5 w-5 animate-spin text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    />
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.372 0 0 5.372 0 12h4z"
                    />
                </svg>
                <span class="text-sm text-gray-600">{{ addTextLoadingText }}</span>
            </div>
        </template>
        <button
            v-else
            @click="onAddTextClick"
            class="flex items-center gap-2 w-1/2 border border-gray-400 hover:bg-gray-300 text-gray-700 px-4 py-3 rounded"
        >
            <IconAddText class="ml-3"/>
            <span class="font-medium">{{ addTextText }}</span>
        </button>
    </div>
    <Modal
        v-model="addImgModalOpened"
        :title="addImageModalTitle"
        :size="'xl'"
    >
        <FileGallery
            :uuid="uuid"
            :extensions="extensions"
            :remove-logo="removeLogo"
            @update:image-data="addImageLayer"
            @update:image-colors="updateColors"
            @update:quality-score="updateQualityScore"
            @close:gallery-modal="closeGalleryModal"
        ></FileGallery>
    </Modal>
</template>

<script setup>

import {ref, onMounted} from "vue";
import Modal from "../Modal.vue";
import FileGallery from "./FileGallery.vue";
import IconAddFile from "../icons/IconAddFile.vue";
import IconAddText from "../icons/IconAddText.vue";
import { useTranslation } from '../../composables/useTranslation.js';

const emit = defineEmits([
    'add:textLayer',
    'add:imageLayer',
    'update:colors',
    'update:quality-score',
])

const props = defineProps({
    uuid: {
        type: String,
        required: false
    },
    extensions: {
        type: Array,
        default: [], // Wartość domyślna
    },
    gravColor: {
        type: String,
        default: null,
    },
    isGraverMode: {
        type: Boolean,
        default: false,
    },
    addedImage: {
        type: Boolean,
        default: false,
    },
    addedText: {
        type: Boolean,
        default: false,
    },
    forcedColor: {
        type: Object,
        default: null,
    },
    removeLogo: {
        type: Number,
        default: 0,
    },
});

const { $t } = useTranslation()

const addImgModalOpened = ref(false)

const addImageText = ref('')
const addTextText = ref('')
const addImageModalTitle = ref('')
const addTextLoadingText = ref('')
const addTextLoading = ref(false)

onMounted(async () => {
    addImageText.value = await $t('buttons.add_image', 'DODAJ GRAFIKĘ')
    addTextText.value = await $t('buttons.add_text', 'DODAJ TEKST')
    addImageModalTitle.value = await $t('image.upload', 'Dodaj grafikę')
    addTextLoadingText.value = await $t('buttons.add_text_loading', 'Ładowanie...')
})

const onAddTextClick = async () => {
    if (props.addedText) return
    if (props.isGraverMode && !props.addedImage && props.gravColor) {
        if (props.forcedColor) {
            emit('add:textLayer', { forcedColor: props.forcedColor })
            return
        }
        addTextLoading.value = true
        try {
            const url = `/api/file/closest-pantone?hex=${encodeURIComponent(props.gravColor)}`
            const res = await fetch(url)
            const data = await res.json().catch(() => ({}))
            if (data.hex != null) {
                emit('add:textLayer', {
                    forcedColor: {
                        hex: data.hex,
                        pantone: data.pantone ?? data.name ?? '',
                        name: data.name ?? data.pantone ?? data.hex,
                    },
                })
            } else {
                emit('add:textLayer')
            }
        } catch (_) {
            emit('add:textLayer')
        } finally {
            addTextLoading.value = false
        }
    } else {
        emit('add:textLayer')
    }
}

const addImageLayer = (data,changedOriginalLogo = false) => {
    emit('add:imageLayer', data,changedOriginalLogo)
    addImgModalOpened.value = false
}
const updateColors = (data) => {
    emit('update:colors', data)
}

const updateQualityScore = (score) => {
    emit('update:quality-score', score)
}

const closeGalleryModal = () => {
    addImgModalOpened.value = false
}
</script>

<style scoped>

</style>
